"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Film, Radio, Search } from "lucide-react";

type VideoHit = {
  slug: string;
  title: string;
  channel_id: string;
  niche: string;
  status: string;
  review_score: number | null;
};

type ChannelHit = {
  id: string;
  name: string;
  niche: string | null;
  video_count: number;
  accent_color?: [number, number, number] | string;
};

function toCssColor(c: ChannelHit["accent_color"]): string | undefined {
  if (!c) return undefined;
  if (typeof c === "string") return c;
  if (Array.isArray(c) && c.length === 3) return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  return undefined;
}

function scoreColor(s: number | null): string {
  if (s == null) return "text-zinc-500";
  if (s >= 8) return "text-emerald-400";
  if (s >= 7) return "text-sky-400";
  if (s >= 6) return "text-amber-400";
  return "text-rose-400";
}

export function GlobalSearch() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [videos, setVideos] = useState<VideoHit[]>([]);
  const [channels, setChannels] = useState<ChannelHit[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // ⌘K / Ctrl+K to toggle
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Load all channels once (small list)
  useEffect(() => {
    fetch("/api/channels")
      .then((r) => r.json())
      .then((d) => setChannels(d.channels ?? []))
      .catch(() => {});
  }, []);

  // Debounced video search
  useEffect(() => {
    if (!open) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const q = query.trim();
    setLoading(true);
    const params = new URLSearchParams({ limit: "12", sort: "created", order: "desc" });
    if (q) params.set("search", q);
    const t = setTimeout(() => {
      fetch(`/api/videos?${params.toString()}`, { signal: ac.signal })
        .then((r) => r.json())
        .then((d) => setVideos(d.data ?? d.videos ?? []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 120);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [query, open]);

  // Reset query when closing
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const q = query.trim().toLowerCase();
  const matchedChannels = q
    ? channels.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          (c.niche ?? "").toLowerCase().includes(q)
      )
    : channels.slice(0, 6);

  function go(href: string) {
    setOpen(false);
    router.push(href);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search"
      description="Find videos, channels, and pages"
      className="max-w-2xl"
    >
      <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search videos and channels..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>
          {loading ? "Searching..." : "No results."}
        </CommandEmpty>

        {matchedChannels.length > 0 && (
          <CommandGroup heading="Channels">
            {matchedChannels.map((c) => (
              <CommandItem
                key={c.id}
                value={`channel ${c.id} ${c.name} ${c.niche ?? ""}`}
                onSelect={() => go(`/channels/${c.id}`)}
              >
                <Radio
                  className="h-4 w-4 shrink-0"
                  style={toCssColor(c.accent_color) ? { color: toCssColor(c.accent_color) } : undefined}
                />
                <span className="font-medium">{c.name}</span>
                <span className="ml-auto text-xs text-zinc-500 font-mono">
                  {c.video_count} {c.video_count === 1 ? "video" : "videos"}
                  {c.niche ? ` · ${c.niche}` : ""}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {videos.length > 0 && (
          <CommandGroup heading={q ? "Videos" : "Recent videos"}>
            {videos.map((v) => (
              <CommandItem
                key={v.slug}
                value={`video ${v.slug} ${v.title} ${v.channel_id} ${v.niche}`}
                onSelect={() => go(`/videos/${v.slug}`)}
              >
                <Film className="h-4 w-4 shrink-0 text-zinc-500" />
                <span className="truncate">{v.title || v.slug}</span>
                <span className="ml-auto flex items-center gap-2 text-xs text-zinc-500 font-mono">
                  {v.review_score != null && (
                    <span className={scoreColor(v.review_score)}>
                      {v.review_score.toFixed(1)}
                    </span>
                  )}
                  <span>{v.channel_id}</span>
                  <span className="uppercase tracking-wider text-[10px] text-zinc-600">
                    {v.status}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
      </Command>
    </CommandDialog>
  );
}

export function GlobalSearchTrigger() {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    setIsMac(/Mac|iPod|iPhone|iPad/.test(navigator.platform));
  }, []);

  function openPalette() {
    const ev = new KeyboardEvent("keydown", {
      key: "k",
      ctrlKey: !isMac,
      metaKey: isMac,
      bubbles: true,
    });
    window.dispatchEvent(ev);
  }

  return (
    <button
      onClick={openPalette}
      className="flex w-full items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-500 hover:border-zinc-700 hover:text-zinc-300 transition-colors"
      aria-label="Open global search"
    >
      <Search className="h-4 w-4 opacity-60" />
      <span>Search...</span>
      <kbd className="ml-auto rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">
        {isMac ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}
